import {
  CreditCard,
  DollarSign,
  Landmark,
  PiggyBank,
  Wallet,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../ui/Badge';
import { Chip } from '../ui/Chip';
import { IconBadge } from '../ui/IconBadge';
import type { FundingMethod } from '../utils/catalog';
import {
  computeFundingBadges,
  type FundingBadge,
} from '../utils/funding-profile';

interface FundingStepProps {
  available: FundingMethod[];
  selected: FundingMethod | null;
  onSelect: (method: FundingMethod) => void;
}

/**
 * Per-method icon mapping. Icons come from lucide-react so they:
 *   1. Inherit color from the parent (`currentColor`), which lets the
 *      `IconBadge` flip between default and accent variants without us
 *      having to manage stroke colors per icon.
 *   2. Are stroke-based and monochrome, matching the rest of the UI's
 *      flat look (no heavy emoji glyphs that render inconsistently across
 *      OSes).
 *
 * Mapping rationale:
 *   - DEBIT_CARD / CREDIT_CARD → same `CreditCard` glyph. The label below
 *     disambiguates; using two different card glyphs would imply they're
 *     physically different objects, which they aren't.
 *   - ACH → `Landmark` (the classic "bank account" pictogram).
 *   - RTP → `Zap` to communicate "instant rail" — same idea as the original
 *     ⚡ glyph but as a proper stroked icon.
 *   - WALLET → `Wallet` (pay-with-balance from a hosted wallet provider).
 *   - CASH_DEPOSIT → `DollarSign`. Explicit product call: the `$`
 *     pictogram is the most universally legible affordance for "cash"
 *     across locales. (`Banknote` from lucide is the more literal
 *     "currency bill" alternative if we ever want the copy to lean
 *     pictorial rather than symbolic.)
 *   - BANK_BALANCE → `PiggyBank` to differentiate from ACH (same underlying
 *     funding source, but the UX framing is "pay from saved balance" vs
 *     "pull money from your bank").
 *
 * Labels and hints are NOT defined here — they come from the `methods.funding.*`
 * i18n bundle so they react to language switches without re-rendering this map.
 */
const FUNDING_ICON: Record<FundingMethod, LucideIcon> = {
  DEBIT_CARD: CreditCard,
  CREDIT_CARD: CreditCard,
  ACH: Landmark,
  RTP: Zap,
  WALLET: Wallet,
  CASH_DEPOSIT: DollarSign,
  BANK_BALANCE: PiggyBank,
};

/**
 * Decorative glyph rendered next to badge labels. Kept here (rather than in
 * the i18n bundle) because it's a presentational concern, not copy.
 */
const BADGE_ICON: Partial<Record<FundingBadge, string>> = {
  best: '★',
  fastest: '⚡',
};

export const FundingStep = ({
  available,
  selected,
  onSelect,
}: FundingStepProps) => {
  const { t } = useTranslation('smart');

  // Recompute when the corridor's funding list changes (e.g. after a route
  // change or when the catalog is re-fetched). Memoizing keeps the badge
  // map stable across re-renders triggered by other context updates.
  const badges = useMemo(() => computeFundingBadges(available), [available]);

  return (
    // `mt-3` reserves vertical headroom for the ribbon-style badge that
    // sits on each chip's top border (see Chip.tsx). Without it, the "Best"
    // / "Cheapest" pills would collide with the accordion's question text.
    <div className="mt-3 grid grid-cols-3 gap-2" role="radiogroup">
      {available.map((m) => {
        const Icon = FUNDING_ICON[m];
        const badgeKind = badges[m];
        return (
          <Chip
            key={m}
            selected={selected === m}
            onClick={() => onSelect(m)}
            icon={
              <IconBadge variant={selected === m ? 'accent' : 'default'}>
                {/* `size={20}` fits comfortably inside the 40px (h-10 w-10)
                  * `IconBadge` md container with ~10px of padding all
                  * around — same visual weight as the previous Unicode
                  * glyphs at `text-lg`. `strokeWidth={2}` is lucide's
                  * default; thinner (1.5) starts to look fragile inside a
                  * tinted badge at this size. */}
                <Icon size={20} strokeWidth={2} aria-hidden />
              </IconBadge>
            }
            label={t(`methods.funding.${m}.label`)}
            description={t(`methods.funding.${m}.hint`)}
            badge={
              badgeKind && (
                <Badge
                  variant={badgeKind}
                  title={t(`methods.badges.${badgeKind}.title`)}
                >
                  {BADGE_ICON[badgeKind] && (
                    <span aria-hidden>{BADGE_ICON[badgeKind]}</span>
                  )}
                  {t(`methods.badges.${badgeKind}.label`)}
                </Badge>
              )
            }
          />
        );
      })}
    </div>
  );
};
